import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import businessPersonFuturistic from '../assets/business-person-futuristic-business-environment.jpg';

const Web3Section = () => {
  const features = [
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      title: "Innovative Technology Integration",
      description: "Leverage cutting-edge Web3 technology to enhance your investment strategies and access new opportunities in the digital economy."
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      title: "Strategic Portfolio Diversification",
      description: "Integrate digital assets and blockchain technology into your existing portfolio to create a balanced, future-ready investment strategy."
    }
  ];

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-br from-gray-50 to-white">
      <div className="section-container">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ 
              duration: 1.2, 
              ease: [0.25, 0.46, 0.45, 0.94],
              delay: 0.2 
            }}
            viewport={{ once: true, margin: "-100px" }}
            className="lg:col-span-1"
          >
            {/* Mobile Image */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
              className="mb-8 lg:hidden"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/20 to-blue-500/20 rounded-xl"></div>
                <img 
                  src={businessPersonFuturistic} 
                  alt="Futuristic business environment with Web3 technology"
                  className="w-full h-[250px] object-cover rounded-xl shadow-lg"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-xl"></div>
              </div>
            </motion.div>

            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
              className="text-3xl lg:text-4xl xl:text-5xl font-medium mb-8 leading-tight text-gray-900"
            >
              Embracing the future
              <br />
              of crypto wealth.
            </motion.h2>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              viewport={{ once: true }}
              className="text-xl text-gray-600 leading-relaxed mb-8"
            >
              Web3 technology represents the next evolution in financial services. We integrate these innovative solutions to provide you with comprehensive, forward-thinking crypto investment strategies and access to new opportunities in the digital economy.
            </motion.p>

                         <motion.div
               initial={{ opacity: 0, y: 20 }}
               whileInView={{ opacity: 1, y: 0 }}
               transition={{ duration: 0.8, delay: 0.8 }}
               viewport={{ once: true }}
               className="space-y-4"
             >
               <Link 
                 to="/web3"
                 className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-medium transition-colors"
               >
                 Explore our crypto solutions
                 <ArrowRightIcon className="w-4 h-4" />
               </Link>
               
               <motion.button
                 whileHover={{ scale: 1.02 }}
                 whileTap={{ scale: 0.98 }}
                 className="bg-gray-900 text-white px-6 py-3 rounded-xl font-medium inline-flex items-center gap-2 hover:bg-gray-800 transition-colors duration-200"
               >
                 <Link to="/apply-now" className="flex items-center gap-2">
                   Create Wallet
                   <ArrowRightIcon className="w-4 h-4" />
                 </Link>
               </motion.button>
             </motion.div>
          </motion.div>

          {/* Center Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ 
              duration: 1.2, 
              ease: [0.25, 0.46, 0.45, 0.94],
              delay: 0.3 
            }}
            viewport={{ once: true, margin: "-100px" }}
            className="lg:col-span-1 hidden lg:block"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/20 to-blue-500/20 rounded-2xl"></div>
              <img 
                src={businessPersonFuturistic} 
                alt="Futuristic business environment with Web3 technology"
                className="w-full h-[400px] object-cover rounded-2xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl"></div>
            </div>
          </motion.div>

          {/* Right Content - Cards */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ 
              duration: 1.2, 
              ease: [0.25, 0.46, 0.45, 0.94],
              delay: 0.4 
            }}
            viewport={{ once: true, margin: "-100px" }}
            className="space-y-6 lg:col-span-1"
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ 
                  duration: 0.8, 
                  delay: 0.6 + index * 0.2 
                }}
                viewport={{ once: true }}
                className="relative bg-white rounded-xl p-6 shadow-lg border border-gray-100 group hover:shadow-xl transition-all duration-300"
              >
                {/* Top section with icon and arrow */}
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                    {feature.icon}
                  </div>
                  
                  <Link 
                    to="/apply-now"
                    className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-gray-200 transition-colors duration-200 group-hover:scale-110"
                  >
                    <ArrowRightIcon className="w-4 h-4 text-gray-600" />
                  </Link>
                </div>
                
                {/* Title */}
                <motion.h3 
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 + index * 0.2 }}
                  viewport={{ once: true }}
                  className="text-xl lg:text-2xl font-bold mb-3 text-gray-900"
                >
                  {feature.title}
                </motion.h3>
                
                {/* Description */}
                <motion.p 
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 1 + index * 0.2 }}
                  viewport={{ once: true }}
                  className="text-gray-600 text-base leading-relaxed"
                >
                  {feature.description}
                </motion.p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Web3Section; 